import type { ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'light' | 'outline-light' | 'outline-dark' | 'whatsapp'
type ButtonSize = 'sm' | 'md' | 'lg'

const variantClasses: Record<ButtonVariant, string> = {
    'primary': 'bg-crimson text-white shadow-[0_12px_32px_-12px_rgba(212,31,58,0.8)] hover:bg-crimson-deep',
    'light': 'bg-white text-ink hover:bg-gold-soft',
    'outline-light': 'border border-white/25 text-white hover:border-white/60 hover:bg-white/10',
    'outline-dark': 'border border-ink/15 text-ink hover:border-ink/40 hover:bg-ink/5',
    'whatsapp': 'bg-whatsapp text-white hover:brightness-110'
}

const sizeClasses: Record<ButtonSize, string> = {
    sm: 'h-9 px-4 text-sm',
    md: 'h-11 px-6 text-sm',
    lg: 'h-13 px-7 text-base'
}

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string => cn(
    'focus-ring inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full font-semibold tracking-tight whitespace-nowrap transition-all duration-300 disabled:pointer-events-none disabled:opacity-60',
    variantClasses[variant],
    sizeClasses[size],
    className
)

interface ButtonLinkProps extends Omit<ComponentProps<typeof Link>, 'className'> {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
    children: ReactNode
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    variant,
    size,
    className,
    children,
    ...props
}): ReactNode => {
    return (
        <Link className={buttonClasses(variant, size, className)} {...props}>
            {children}
        </Link>
    )
}

interface ButtonAnchorProps extends Omit<ComponentProps<'a'>, 'className'> {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
    children: ReactNode
}

export const ButtonAnchor: FC<ButtonAnchorProps> = ({
    variant,
    size,
    className,
    children,
    ...props
}): ReactNode => {
    return (
        <a className={buttonClasses(variant, size, className)} {...props}>
            {children}
        </a>
    )
}
